import type { LastUsedMethod } from './lastUsedWebAuthMethod';

/**
 * The provider whose account the seller's own client suggests they already hold.
 *
 * A subset of {@link LastUsedMethod} rather than its own union, so a renamed
 * provider fails to compile here instead of quietly never matching a button.
 */
export type ProviderPreference = Extract<LastUsedMethod, 'google' | 'apple'>;

type Rule = readonly [ProviderPreference, readonly string[]];

/**
 * Recognized clients that are not a provider signal, checked ahead of
 * {@link BROWSER_RULES} because their user agents also carry `Chrome/`. A match
 * defers to the OS rules rather than deciding: Firefox on Android still says
 * something about the account, the browser brand just isn't what says it.
 *
 * Current Brave omits its own token to mimic Chrome, so it classifies as Chrome.
 * That is not fixable from the user agent and costs only order, never access.
 */
const NON_SIGNAL_BROWSER_TOKENS: readonly string[] = [
  'Brave/',
  'Edg/',
  'Edge/',
  'EdgA/',
  'EdgiOS/',
  'Firefox/',
  'FxiOS/',
];

/**
 * Embedded browsers are deliberately left unclassified. Google refuses OAuth
 * from an embedded webview, so hoisting Google inside one would promote the path
 * least able to finish.
 */
const WEBVIEW_TOKENS: readonly string[] = [
  'FBAN/',
  'FBAV/',
  'FB_IAB',
  'Instagram',
  'Line/',
  '; wv',
];

/**
 * The browser outranks the device: a seller in Chrome on a Mac is signed into
 * Google in the window they are looking at, whatever Apple ID the machine holds.
 *
 * Google's tokens are checked first because every WebKit-derived user agent
 * carries `Safari/`, including Chrome's.
 */
const BROWSER_RULES: readonly Rule[] = [
  ['google', ['Chrome/', 'CriOS/', 'Chromium/']],
  ['apple', ['Safari/']],
];

/**
 * `Macintosh` rather than the substring `Mac OS X`, which iOS user agents also
 * carry via "like Mac OS X". Android reports `Linux`, which is why there is no
 * Linux row: a desktop Linux client tells us nothing about either account.
 */
const OS_RULES: readonly Rule[] = [
  ['apple', ['iPhone', 'iPad', 'iPod', 'Macintosh']],
  ['google', ['Android']],
];

/**
 * The provider to show first for a given client, or undefined for a client we
 * decline to classify — which keeps the declared stack order.
 *
 * Token matching over the user agent rather than User-Agent Client Hints:
 * `navigator.userAgentData` exists only in Chromium, the one family a browser
 * token already settles, so it would add a second code path that decides
 * nothing new. The tokens and family boundaries mirror `Humans::UserAgentFamily`
 * (`rb/services/web/app/lib/humans/user_agent_family.rb`), which tags the auth
 * funnel's browser and OS metrics, so an experiment on this ordering reads on
 * the cut that telemetry already publishes.
 */
export function preferredProviderForUserAgent(
  userAgent: string | undefined,
): ProviderPreference | undefined {
  if (userAgent === undefined || userAgent === '') {
    return undefined;
  }
  if (contains(userAgent, WEBVIEW_TOKENS)) {
    return undefined;
  }
  if (!contains(userAgent, NON_SIGNAL_BROWSER_TOKENS)) {
    const byBrowser = firstMatch(userAgent, BROWSER_RULES);
    if (byBrowser !== undefined) {
      return byBrowser;
    }
  }
  return firstMatch(userAgent, OS_RULES);
}

/**
 * The rendering client's preference. Undefined off the browser, where there is
 * no client to read — a server render must paint the declared order.
 */
export function readClientProviderPreference(): ProviderPreference | undefined {
  if (typeof navigator === 'undefined') {
    return undefined;
  }
  return preferredProviderForUserAgent(navigator.userAgent);
}

function contains(userAgent: string, tokens: readonly string[]): boolean {
  return tokens.some((token) => userAgent.includes(token));
}

function firstMatch(userAgent: string, rules: readonly Rule[]): ProviderPreference | undefined {
  return rules.find(([, tokens]) => contains(userAgent, tokens))?.[0];
}
